import { FirehoseClient, PutRecordCommand, PutRecordCommandInput } from '@aws-sdk/client-firehose';
import { fromCognitoIdentity, fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import { fromBase64 } from '@smithy/util-base64';

import { ENV, SYSTEMS_NAMES } from '../constants';
import DataProvider from '../DataProvider';
import Utils from '../helpers/Utils';
import { Config } from '../Interfaces/Config';
import { IPrepareTrackParams, ITrackNewAnalytics } from '../Interfaces/Events';
import { EventActionType } from '../types';

import AbstractSystem from './AbstractSystem';

const REGION = 'us-east-1';

export default class AwsAnalytic extends AbstractSystem {
    private streamName: string;
    private envMode: string;
    private firehose: FirehoseClient | undefined;
    private releaseDate: string;

    constructor() {
        super(SYSTEMS_NAMES.amazon, false);

        this.streamName = 'aws_stream_name';
        this.envMode = ENV.develop;
        this.releaseDate = 'default_date';
        this.firehose = undefined;
    }

    public init({ id, config }: { id: string; config?: Config }) {
        try {
            if (!this.getIsInit() && config?.streamName) {
                Utils.setReleaseDate(config.releaseDate);

                this.streamName = config.streamName;
                this.envMode = config.envMode;
                this.releaseDate = config.releaseDate;

                const identityId = config.awsId as string;

                const credentials = identityId
                    ? fromCognitoIdentity({
                          identityId,
                          clientConfig: { region: REGION },
                      })
                    : fromCognitoIdentityPool({
                          identityPoolId: id,
                          clientConfig: { region: REGION },
                      });

                this.firehose = new FirehoseClient({
                    region: REGION,
                    credentials,
                });

                this.isInit = true;
            }
        } catch (e) {
            DataProvider.handleError(e);
        }
    }

    public async trackNew({ eventName, eventAction, eventData = {} }: ITrackNewAnalytics): Promise<void> {
        if (this.firehose !== undefined) {
            const command = new PutRecordCommand(await this.prepareTrackParams({ eventName, eventAction, eventData }));

            try {
                await this.firehose.send(command);

                // eslint-disable-next-line no-console
                console.log(`Event ${eventName} successfully sent.`);
            } catch (err) {
                DataProvider.handleError(err);

                console.error(`Error while sending ${eventName}`);
                // @ts-ignore
                console.error(err, err.stack);
            }
        }
    }

    public async getAwsId(): Promise<string> {
        const credentials = await this.firehose?.config.credentials();

        if (!credentials || typeof credentials !== 'object') {
            return '';
        }

        const creds = credentials as { identityId?: string };

        return creds.identityId ?? '';
    }

    public async getDefaultRecordData(eventName = 'purchase', eventAction?: EventActionType) {
        const awsData = await Utils.getAnalyticData(eventName, eventAction);
        const aws_id = await this.getAwsId();

        const OnetrustActiveGroups = Utils.formatOptanonGroups(window?.OptanonActiveGroups);

        return {
            ...awsData,
            aws_id,
            OnetrustActiveGroups,
        };
    }

    private async prepareTrackParams({
        eventName,
        eventAction,
        eventData,
    }: IPrepareTrackParams): Promise<PutRecordCommandInput> {
        const recordData = await this.getDefaultRecordData(eventName, eventAction);

        const finalRecordData = Object.assign({}, recordData, eventData, {
            mode: this.envMode,
            release_date: this.releaseDate,
        });

        const stringifyRecordData = JSON.stringify(finalRecordData);
        const convertData = btoa(unescape(encodeURIComponent(stringifyRecordData)));

        // eslint-disable-next-line no-console
        console.log('BEFORE SEND TO AMAZON DATA', finalRecordData);

        return {
            DeliveryStreamName: this.streamName,
            Record: { Data: fromBase64(convertData) },
        };
    }
}
